// Copyright (c) StrangeBee 2023

import { ExtractProps } from '@Molecules/Details/Details';
import React from 'react';
import { UserOutput, ProfileOutput, PasswordPolicyOutput, SessionOutput, UserTypeValues } from 'thehive-sdk';
import { GlobalUserOrgSelector } from './GlobalUserOrgSelector/GlobalUserOrgSelector';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { FormatDate, FormatPermissions } from '@Molecules/Format';
import { UserEventList } from '@Organisms/User/eventList';
import UploadAvatar from '@Atoms/UploadAvatar/UploadAvatar';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { UseLocalMessageType } from '@Helpers/locale';
import THSelector from '@Molecules/THSelector/THSelector';
import { APIKeyButton } from '@Molecules/APIKeyButton/APIKeyButton';
import { GlobalUserPassword } from './GlobalUserPassword/GlobalUserPassword';
import { AuthEventList } from '@Organisms/AccountDetails/eventList';
import { GlobalUserMFA } from './GlobalUserMFA/GlobalUserMFA';

import './extractData.scss';

interface ExtractDataProps {
    data: UserOutput;
    locale: UseLocalMessageType;
    eventList: UserEventList;
    authEventList: AuthEventList;
}

export const extractData = ({
    data: { _id: userId, defaultOrganisation },
    locale,
    eventList,
    authEventList,
}: ExtractDataProps): Record<string, ExtractProps> => ({
    avatar: {
        className: 'user-details__avatar',
        isForce: true,
        render: () => (
            <THForm.Item name="avatar">
                <UploadAvatar />
            </THForm.Item>
        ),
    },
    name: {
        className: 'user-details__name',
        render: () => (
            <THForm.Item
                name="name"
                label={locale('common.name')}
                rules={[
                    {
                        required: true,
                        message: locale('user.create.form.name.required'),
                    },
                ]}
            >
                <THInput borderless placeholder={locale('common.name')} />
            </THForm.Item>
        ),
    },
    login: {
        className: 'user-details__section',
        render: () => (
            <THForm.Item name="login" label={locale('common.login')}>
                <THInput disabled borderless placeholder={locale('common.login')} />
            </THForm.Item>
        ),
    },
    email: {
        isForce: true,
        className: 'user-details__section',
        render: () => (
            <THForm.Item name="email" label={locale('common.email')}>
                <THInput borderless placeholder={locale('common.email')} />
            </THForm.Item>
        ),
    },
    type: {
        isForce: true,
        className: 'user-details__section',
        render: () => (
            <THForm.Item name="type" label={locale('common.type')}>
                <THSelect borderless options={UserTypeValues.map((v) => ({ label: v, value: v }))} />
            </THForm.Item>
        ),
    },
    locked: {
        render: () => (
            <THForm.Item name="locked" label={locale('common.locked')} valuePropName="checked">
                <THSwitch />
            </THForm.Item>
        ),
    },
    hasMFA: {
        name: locale('user.mfa'),
        className: 'user-details__section',
        isForce: true,
        render: (hasMFA: boolean) => <GlobalUserMFA hasMFA={hasMFA} authEventList={authEventList} userId={userId} />,
    },
    hasKey: {
        name: locale('user.list.table.columns.api'),
        isForce: true,
        className: 'user-details__section',
        render: (hasKey: boolean) => (
            <APIKeyButton
                hasKey={hasKey}
                userId={userId}
                onGet={eventList.onGetAPIKey}
                onRenew={eventList.onCreateAPIKey}
                onRevoke={eventList.onRevokeAPIKey}
            />
        ),
    },
    '#computeProfile': {
        className: 'user-details__section',
        render: ({
            profileList,
            refetchProfileList,
        }: {
            profileList: ProfileOutput[];
            refetchProfileList: () => void;
        }) => (
            <THForm.Item label={locale('common.profile')} name="profile">
                <THSelect
                    borderless
                    options={profileList.map(({ name }) => ({ label: name, value: name }))}
                    onDropdownVisibleChange={(open) => open && refetchProfileList()}
                />
            </THForm.Item>
        ),
    },
    '#computePermissions': {
        className: 'user-details__section',
        render: ({ profileList }: { profileList: ProfileOutput[] }) => (
            <THForm.Item<UserOutput> noStyle shouldUpdate={(prev, curr) => prev.profile !== curr.profile}>
                {({ getFieldValue }) => {
                    const newPermissions =
                        profileList.find((profile) => profile.name === getFieldValue('profile'))?.permissions || [];

                    return (
                        <THForm.Item
                            label={locale('user.create.form.permissions.label')}
                            hidden={!getFieldValue('profile')}
                        >
                            <FormatPermissions permissions={newPermissions} permissionsOnly />
                        </THForm.Item>
                    );
                }}
            </THForm.Item>
        ),
    },
    '#computePassword': {
        className: 'user-details__section',
        render: ({
            passwordRule,
            validator,
            hasPassword,
        }: {
            passwordRule: PasswordPolicyOutput;
            validator: (str: any) => any;
            hasPassword: boolean;
        }) => <GlobalUserPassword passwordRule={passwordRule} hasPassword={hasPassword} validator={validator} />,
    },
    '#computeResetPassword': {
        className: 'user-details__reset-password',
        render: ({ resetPassword }: { resetPassword: () => void }) => (
            <THButton type="default" onClick={() => resetPassword()}>
                {locale('user.drawer.reset-password.label')}
            </THButton>
        ),
    },
    'extraData.organisations': {
        className: 'user-details__section',
        render: () => (
            <THForm.Item
                name="organisations"
                label={locale('common.organisations')}
                rules={[
                    {
                        validator: (_, value) => {
                            if (value.length === 0) {
                                return Promise.reject(locale('user.create.form.organisations.required'));
                            }
                            return Promise.resolve();
                        },
                    },
                ]}
            >
                <GlobalUserOrgSelector userDefaultOrganisation={defaultOrganisation} exceededQuota={false} />
            </THForm.Item>
        ),
    },
    '#computeSessions': {
        name: locale('common.sessions'),
        className: 'user-details__section',
        render: ({
            sessionList,
            onDelete,
            onSuccess,
        }: {
            sessionList: SessionOutput[];
            onDelete?: (sessionId: string) => Promise<void>;
            onSuccess: () => void;
        }) => (
            <THSelector<SessionOutput> refKey="id" dataSource={sessionList} disabled>
                {({ startDate, expirationDate, id }) => (
                    <div className="user-details__sessions__row">
                        <div>
                            {locale('user.sessions.started')} <FormatDate date={startDate} onlyFrom />{' '}
                            {locale('user.sessions.expires')} <FormatDate date={expirationDate} />
                        </div>

                        {onDelete && (
                            <div>
                                <THButton type="link" danger onClick={() => onDelete(id).then(onSuccess)}>
                                    {locale('general.delete')}
                                </THButton>
                            </div>
                        )}
                    </div>
                )}
            </THSelector>
        ),
    },
});
